import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import companyService from '../services/company.service';
import { useAuth } from './authContext';
import employeeService from '../services/employee.service';
import * as serviceService from '../services/service.service';

const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
    const { user } = useAuth();
    // Inicializar desde localStorage para evitar flicker (card vacío → card cargado)
    const [companyInfo, setCompanyInfo] = useState(() => {
        try {
            const saved = localStorage.getItem('dashCtx_company');
            return saved ? JSON.parse(saved) : null;
        } catch (e) { return null; }
    });
    const [branches, setBranches] = useState([]);
    const [staff, setStaff] = useState([]);
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const lastFetchedRef = React.useRef(0);

    // Depender de "hay sesión" y no del objeto user: authContext puede reemplazar el objeto
    // (caché local → /api/auth/me) y eso re-disparaba toda la carga (TASK-122)
    const hasUser = Boolean(user);

    const fetchBaseData = useCallback(async (force = false, signal) => {
        const now = Date.now();
        // Cache in-memory de 30 segundos para evitar refetching en cada click de navegacion
        if (!force && (now - lastFetchedRef.current < 30000) && lastFetchedRef.current > 0) return;

        if (!hasUser) return;

        setIsLoading(true);
        try {
            // Un solo bloque de peticiones en paralelo para TODO el dashboard.
            // Pasan por requestCache: las páginas que piden lo mismo reutilizan estas peticiones.
            // force (refresco periódico o manual) ignora la caché de empresa y sucursales.
            const [companyRes, branchesRes, staffRes, servicesRes] = await Promise.all([
                companyService.getCompany(signal, { force }),
                companyService.getBranches(signal, { force }),
                // Si falla solo el personal no se aborta el resto de la carga base: se devuelve null
                // y se conserva el personal anterior en lugar de vaciarlo (TASK-123).
                // La cancelación por desmontaje sí se propaga para no tocar el estado (TASK-122)
                Promise.resolve(employeeService?.getAll ? employeeService.getAll(null, signal) : []).catch(err => {
                    if (err?.name === 'AbortError') throw err;
                    console.warn('[Dashboard] No se pudo actualizar el personal:', err?.message || err);
                    return null;
                }),
                serviceService.getServices(null, null, signal)
            ]);

            const freshCompany = companyRes?.company || companyRes || null;
            if (freshCompany) setCompanyInfo(freshCompany);
            // Cachear en localStorage para evitar flicker en la próxima carga
            try { localStorage.setItem('dashCtx_company', JSON.stringify(freshCompany)); } catch (e) { /* silencioso */ }
            setBranches(Array.isArray(branchesRes) ? branchesRes : []);
            // null = falló la carga del personal: se mantiene el último valor conocido
            if (staffRes !== null) setStaff(Array.isArray(staffRes) ? staffRes : []);
            setServices(Array.isArray(servicesRes) ? servicesRes : (servicesRes?.services || []));
            lastFetchedRef.current = now;

            // Función auxiliar para verificar si el onboarding fue completado de forma flexible (boolean, int, string)
            const isCompleted = freshCompany && (freshCompany.onboarding_completed === true || freshCompany.onboarding_completed === 1 || freshCompany.onboarding_completed === 'true');

            // Si la empresa aún no completó el onboarding y estamos dentro del dashboard, redirigir a /onboarding
            if (freshCompany && !isCompleted && window.location.pathname !== '/onboarding') {
                window.location.href = '/onboarding';
            }
        } catch (error) {
            // La cancelación por desmontaje (StrictMode o logout) no es un error
            if (error?.name === 'AbortError') return;
            console.error("DashboardContext fetch error:", error);
        } finally {
            // Si se canceló, el provider ya no está montado: no tocar el estado
            if (!signal?.aborted) setIsLoading(false);
        }
    }, [hasUser]);

    useEffect(() => {
        if (!hasUser) return undefined;
        // AbortController compartido por la carga inicial y los refrescos de este montaje
        const controller = new AbortController();
        fetchBaseData(false, controller.signal);
        // Refrescar cada 5 mins silenciosamente
        const interval = setInterval(() => fetchBaseData(true, controller.signal), 300000);
        return () => {
            clearInterval(interval);
            controller.abort();
        };
    }, [hasUser, fetchBaseData]);

    // TASK-162: referencia estable de refreshData para no invalidar efectos de los consumidores
    const refreshData = useCallback(() => fetchBaseData(true), [fetchBaseData]);

    // TASK-162: memoizar el value evita que TODOS los consumidores de useDashboard()
    // se re-rendericen cuando el provider se renderiza sin que cambien los datos
    const value = useMemo(() => ({
        companyInfo,
        branches,
        staff,
        services,
        isLoading,
        refreshData
    }), [companyInfo, branches, staff, services, isLoading, refreshData]);


    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};

// TASK-148: variante que no exige el provider (componentes que también se montan sueltos)
export const useOptionalDashboard = () => useContext(DashboardContext) || null;

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboard debe ser usado dentro de un DashboardProvider');
    }
    return context;
};
